import type { FormProps } from "antd"
import { Form, message, Space, Spin } from "antd"
import Title from "antd/es/typography/Title"
import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { AttributesRecruitment } from "../../ui-builder"
import { fetchRecruitmentId, updateRecruitment } from "../services/recruitment"
import Department from "./Departments"
import Publish from "./Publish"
import RecruitmentForm from "./RecruitmentForm"

const EditRecruitment = () => {
  const [status, setStatus] = useState("Draft")
  const [quillValue, setQuillValue] = useState<string>("")
  const [language, setLanguages] = useState("vi")
  const [publishDate, setPublishDate] = useState<string | null>(null)
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [recruitment, setRecruitment] = useState<AttributesRecruitment | null>(null)
  const [form] = Form.useForm()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const handleFetchRecruitment = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        const response = await fetchRecruitmentId(id)
        setRecruitment(response)
        if (response.status === "Published") {
          setStatus("Publish")
        } else {
          setStatus("Draft")
        }
        form.setFieldsValue(response.attributes)
        setSelectedDepartment(response.attributes.Department ? response.attributes.Department : "")
        setLanguages(response.language || "vi")
        setLoading(false)
        setQuillValue(response.attributes.Content)
      } catch {
        message.error(t("Error fetching recruitment"))
      }
    },
    [form],
  )

  const onFinish: FormProps<AttributesRecruitment>["onFinish"] = async values => {
    const dataPayload = {
      Title: values.Title,
      Content: values.Content,
      Department: selectedDepartment || "",
      Rank: values.Rank,
      Quantity: values.Quantity,
      Form: values.Form,
    }

    try {
      await updateRecruitment(id || "", dataPayload)
      message.success(t("Recruitment updated successfully!"))
      form.resetFields()
      navigate(Paths.RECRUITMENT.LIST.PATH)
    } catch (error) {
      message.error(t("Failed to update recruitment. Please try again."))
    }
  }

  const onFinishFailed: FormProps<AttributesRecruitment>["onFinishFailed"] = () => {
    message.error(t("Please check the form and fix any errors."))
  }

  const onChangeDepartment = (checkedValues: string) => {
    setSelectedDepartment(checkedValues)
  }

  useEffect(() => {
    if (id) {
      handleFetchRecruitment(Number(id))
    }
  }, [id, handleFetchRecruitment])

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center min-h-[500px]">
          <Spin size="large" />
        </div>
      ) : (
        <div className="p-4">
          <Space>
            <Title level={2}>{t("Edit Recruitment")}</Title>
          </Space>
          <div className="flex justify-between">
            <RecruitmentForm form={form} onFinish={onFinish} onFinishFailed={onFinishFailed} quillValue={quillValue} />
            <div className="w-[22%] pt-2">
              <Publish
                onSubmit={form.submit}
                isCreateEdit={false}
                setStatus={setStatus}
                language={language}
                setLanguages={setLanguages}
                status={status}
                setPublishDate={setPublishDate}
              />
              <Department selectedDepartment={selectedDepartment || ""} onChangeDepartment={onChangeDepartment} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default EditRecruitment
